import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'ru' | 'ua' | 'pl';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getPath: (path: string) => string;
};
const translations = {
  en: {
    // Navigation
    'nav.getStarted': 'Get a Free Demo',
    'nav.home': 'Home',
    'nav.services': 'Pricing',
    'nav.referral': 'Referral Program',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    // Cookie Banner
    'cookie.title': 'We use cookies',
    'cookie.description': 'We use cookies to improve your experience on our site, analyze traffic, and personalize content.',
    'cookie.accept': 'Accept All',
    'cookie.decline': 'Decline',
    'cookie.learnMore': 'Learn More',
    // Hero
    'hero.badge': 'Professional Web Agency',
    'hero.title': 'Modern Websites',
    'hero.titleHighlight': 'for €299',
    'hero.subtitle': "Get a free custom demo first. Pay only when you're ready to launch.",
    'hero.bullet1': 'Free demo preview before you pay',
    'hero.bullet2': 'One-time payment: €299',
    'hero.bullet3': 'Worldwide service',
    'hero.cta': 'Get a Free Demo',
    'hero.referral': 'Referral Program',
    // Pricing
    'pricing.title': 'How pricing',
    'pricing.titleHighlight': 'works',
    'pricing.subtitle': 'Free demo first. Pay only if you want to launch.',
    'pricing.demo.title': 'Free Demo Preview',
    'pricing.demo.label': 'FREE',
    'pricing.demo.desc': 'See your website before you pay.',
    'pricing.demo.b1': 'Custom preview based on your project',
    'pricing.demo.b2': 'No commitment',
    'pricing.demo.b3': "If you don't like it — you don't pay",
    'pricing.demo.cta': 'Get a Free Demo',
    'pricing.launch.title': 'Website Launch',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(one-time)',
    'pricing.launch.desc': 'Publish your website and start receiving leads.',
    'pricing.launch.b1': 'Modern design (desktop + mobile)',
    'pricing.launch.b2': 'Domain connection & go-live setup',
    'pricing.launch.b3': 'Lead form + notifications',
    'pricing.launch.cta': 'Launch for €299',
    // How It Works
    'how.title': 'How it',
    'how.titleHighlight': 'works',
    'how.step1.title': 'Tell us about your project',
    'how.step1.note': 'Takes about 2 minutes',
    'how.step2.title': 'We build your demo',
    'how.step2.note': 'No payment required',
    'how.step3.title': 'You request changes',
    'how.step3.note': 'We adjust until you love it',
    'how.step4.title': 'You approve & pay €299',
    'how.step4.note': 'Only when you are 100% happy',
    'how.step5.title': 'We connect your domain & launch',
    'how.step5.note': 'Your site goes live',
    'how.note': "If you don't like the demo — you don't pay.",
    // FAQ
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    'faq.subtitle': 'Everything you need to know about working with our team',
    'faq.q1': 'What is the free demo?',
    'faq.a1': 'A preview version of your website based on your business and goals. You review it before paying.',
    'faq.q2': 'How long does it take to receive the demo?',
    'faq.a2': "You'll receive your demo preview in 1–3 days.",
    'faq.q3': 'Can I request changes to the demo?',
    'faq.a3': "Yes. Send us your feedback — we'll update the demo for you.",
    'faq.q4': 'What happens after I pay €299?',
    'faq.a4': 'We finalize the website, publish it, connect your domain, and enable the contact/lead form.',
    'faq.q5': 'Do you work worldwide?',
    'faq.a5': 'Yes. We work with clients in any country and any industry.',
    // Testimonials
    'testimonials.title': 'What clients',
    'testimonials.titleHighlight': 'say',
    'testimonials.subtitle': 'Real feedback from businesses we helped launch online',
    'testimonials.r1.name': 'Michael',
    'testimonials.r1.text': 'Incredible value for €299! Got a stunning website in just 2 days. The quality exceeded my expectations completely.',
    'testimonials.r1.business': 'Fitness Studio Owner',
    'testimonials.r2.name': 'Sarah',
    'testimonials.r2.text': 'I was skeptical at first, but the free demo convinced me. Professional design, fast delivery, amazing price!',
    'testimonials.r2.business': 'Bakery Owner',
    'testimonials.r3.name': 'David',
    'testimonials.r3.text': 'Best decision for my business. €299 for this quality is unbelievable. Already getting new clients through the website!',
    'testimonials.r3.business': 'Photography Studio',
    // Consultation
    'consultation.title': 'Get your',
    'consultation.titleHighlight': 'free demo',
    'consultation.subtitle': "Tell us what you need — we'll send a demo preview link.",
    'consultation.benefit1': 'Free preview',
    'consultation.benefit2': 'Fast delivery',
    'consultation.benefit3': 'No commitment',
    'consultation.name': 'Name',
    'consultation.namePlaceholder': 'Your name',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'your@email.com',
    'consultation.desc': 'Project description',
    'consultation.descPlaceholder': 'Tell us about your business and what kind of website you need...',
    'consultation.cta': 'Get a Free Demo',
    'consultation.note': "No commitment. If you don't like the demo — you don't pay.",
    'consultation.sending': 'Sending...',
    'consultation.or': 'or talk to our team directly',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Services',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.aboutLink': 'About IT Monsters',
    // Stats
    'stats.websites': 'Websites Launched',
    'stats.clients': 'Happy Clients',
    'stats.years': 'Years Experience',
    'stats.title': 'Our',
    'stats.titleHighlight': 'Impact',
    'stats.subtitle': 'Delivering results for businesses worldwide'
  },
  ru: {
    // Navigation
    'nav.getStarted': 'Получить бесплатное демо',
    'nav.home': 'Главная',
    'nav.services': 'Цены',
    'nav.referral': 'Реферальная программа',
    'nav.privacy': 'Политика конфиденциальности',
    'nav.terms': 'Условия использования',
    // Cookie Banner
    'cookie.title': 'Мы используем cookies',
    'cookie.description': 'Мы используем cookies для улучшения вашего опыта на сайте, анализа трафика и персонализации контента.',
    'cookie.accept': 'Принять все',
    'cookie.decline': 'Отклонить',
    'cookie.learnMore': 'Подробнее',
    // Hero
    'hero.badge': 'Профессиональное веб-агентство',
    'hero.title': 'Современные сайты',
    'hero.titleHighlight': 'за €299',
    'hero.subtitle': 'Сначала получите бесплатное демо. Платите только если хотите запустить сайт.',
    'hero.bullet1': 'Бесплатное демо перед оплатой',
    'hero.bullet2': 'Разовый платеж €299',
    'hero.bullet3': 'Работаем по всему миру',
    'hero.cta': 'Получить бесплатное демо',
    'hero.referral': 'Реферальная программа',
    // Pricing
    'pricing.title': 'Как работают',
    'pricing.titleHighlight': 'цены',
    'pricing.subtitle': 'Сначала бесплатное демо. Платите только если хотите запустить.',
    'pricing.demo.title': 'Бесплатное демо',
    'pricing.demo.label': 'БЕСПЛАТНО',
    'pricing.demo.desc': 'Посмотрите свой сайт до оплаты.',
    'pricing.demo.b1': 'Индивидуальное демо под ваш проект',
    'pricing.demo.b2': 'Без обязательств',
    'pricing.demo.b3': 'Если не понравится — вы не платите',
    'pricing.demo.cta': 'Получить бесплатное демо',
    'pricing.launch.title': 'Запуск сайта',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(разово)',
    'pricing.launch.desc': 'Мы публикуем сайт — и вы начинаете получать заявки.',
    'pricing.launch.b1': 'Современный дизайн (ПК + мобильный)',
    'pricing.launch.b2': 'Подключение домена + запуск',
    'pricing.launch.b3': 'Форма заявок + уведомления',
    'pricing.launch.cta': 'Запустить за €299',
    // How It Works
    'how.title': 'Как это',
    'how.titleHighlight': 'работает',
    'how.step1.title': 'Расскажите о вашем проекте',
    'how.step1.note': 'Около 2 минут',
    'how.step2.title': 'Мы создаем демо',
    'how.step2.note': 'Оплата не требуется',
    'how.step3.title': 'Вы запрашиваете правки',
    'how.step3.note': 'Вносим правки, пока вам не понравится',
    'how.step4.title': 'Вы одобряете и платите €299',
    'how.step4.note': 'Только когда вы на 100% довольны',
    'how.step5.title': 'Подключаем домен и запускаем',
    'how.step5.note': 'Ваш сайт выходит в онлайн',
    'how.note': 'Если демо не понравится — вы не платите.',
    // FAQ
    'faq.title': 'Часто задаваемые',
    'faq.titleHighlight': 'вопросы',
    'faq.subtitle': 'Всё, что нужно знать о работе с нами',
    'faq.q1': 'Что такое бесплатное демо?',
    'faq.a1': 'Это предварительная версия вашего сайта, основанная на вашем бизнесе и целях. Вы смотрите её до оплаты.',
    'faq.q2': 'Как быстро я получу демо?',
    'faq.a2': 'Вы получите демо-версию через 1–3 дня.',
    'faq.q3': 'Можно ли просить правки в демо?',
    'faq.a3': 'Да. Присылайте фидбек — мы обновим демо для вас.',
    'faq.q4': 'Что происходит после оплаты €299?',
    'faq.a4': 'Мы финализируем сайт, публикуем его, подключаем ваш домен и включаем форму заявок.',
    'faq.q5': 'Вы работаете по всему миру?',
    'faq.a5': 'Да. Мы работаем с клиентами из любой страны и любой сферы.',
    // Testimonials
    'testimonials.title': 'Что говорят',
    'testimonials.titleHighlight': 'клиенты',
    'testimonials.subtitle': 'Реальные отзывы компаний, которым мы помогли выйти в онлайн',
    'testimonials.r1.name': 'Михаил',
    'testimonials.r1.text': 'Отличное соотношение цены и качества за €299! Получил потрясающий сайт всего за 2 дня. Качество превзошло все мои ожидания.',
    'testimonials.r1.business': 'Владелец фитнес-студии',
    'testimonials.r2.name': 'Анна',
    'testimonials.r2.text': 'Сначала сомневалась, но бесплатное демо убедило меня. Профессиональный дизайн, быстрые сроки, отличная цена!',
    'testimonials.r2.business': 'Владелица пекарни',
    'testimonials.r3.name': 'Дмитрий',
    'testimonials.r3.text': 'Лучшее решение для моего бизнеса. €299 за такое качество — это невероятно. Уже получаю новых клиентов с сайта!',
    'testimonials.r3.business': 'Фотостудия',
    // Consultation
    'consultation.title': 'Получите бесплатное демо',
    'consultation.titleHighlight': '',
    'consultation.subtitle': 'Расскажите, что вам нужно — мы пришлем ссылку на демо.',
    'consultation.benefit1': 'Бесплатный просмотр',
    'consultation.benefit2': 'Быстрая доставка',
    'consultation.benefit3': 'Без обязательств',
    'consultation.name': 'Имя',
    'consultation.namePlaceholder': 'Ваше имя',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Описание проекта',
    'consultation.descPlaceholder': 'Расскажите о вашем бизнесе и о том, какой сайт вам нужен…',
    'consultation.cta': 'Получить бесплатное демо',
    'consultation.note': 'Без обязательств. Если демо не понравится — вы не платите.',
    'consultation.sending': 'Отправка...',
    'consultation.or': 'или напишите нам напрямую',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Услуги',
    'footer.company': 'Компания',
    'footer.legal': 'Юридическое',
    'footer.contact': 'Контакты',
    'footer.rights': 'Все права защищены.',
    'footer.aboutLink': 'О IT Monsters',
    // Stats
    'stats.websites': 'Сайтов запущено',
    'stats.clients': 'Довольных клиентов',
    'stats.years': 'Лет опыта',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вклад',
    'stats.subtitle': 'Результаты для бизнеса по всему миру'
  },
  ua: {
    // Navigation
    'nav.getStarted': 'Отримати безкоштовне демо',
    'nav.home': 'Головна',
    'nav.services': 'Ціни',
    'nav.referral': 'Реферальна програма',
    'nav.privacy': 'Політика конфіденційності',
    'nav.terms': 'Умови використання',
    // Cookie Banner
    'cookie.title': 'Ми використовуємо cookies',
    'cookie.description': 'Ми використовуємо cookies для покращення вашого досвіду на сайті, аналізу трафіку та персоналізації контенту.',
    'cookie.accept': 'Прийняти все',
    'cookie.decline': 'Відхилити',
    'cookie.learnMore': 'Детальніше',
    // Hero
    'hero.badge': 'Професійне веб-агентство',
    'hero.title': 'Сучасні сайти',
    'hero.titleHighlight': 'за €299',
    'hero.subtitle': 'Спочатку отримайте безкоштовне демо. Платіть тільки якщо хочете запустити сайт.',
    'hero.bullet1': 'Безкоштовне демо перед оплатою',
    'hero.bullet2': 'Одноразово €299',
    'hero.bullet3': 'Працюємо по всьому світу',
    'hero.cta': 'Отримати безкоштовне демо',
    'hero.referral': 'Реферальна програма',
    // Pricing
    'pricing.title': 'Ціни та',
    'pricing.titleHighlight': 'умови',
    'pricing.subtitle': 'Спочатку безкоштовне демо. Платіть тільки якщо хочете запустити.',
    'pricing.demo.title': 'Безкоштовне демо',
    'pricing.demo.label': 'БЕЗКОШТОВНО',
    'pricing.demo.desc': 'Побачте ваш сайт до оплати.',
    'pricing.demo.b1': 'Індивідуальне демо під ваш проєкт',
    'pricing.demo.b2': "Без зобов'язань",
    'pricing.demo.b3': 'Якщо не сподобається — ви не платите',
    'pricing.demo.cta': 'Отримати безкоштовне демо',
    'pricing.launch.title': 'Запуск сайту',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(одноразово)',
    'pricing.launch.desc': 'Опублікуйте сайт і почніть отримувати заявки.',
    'pricing.launch.b1': 'Сучасний дизайн (десктоп і мобільний)',
    'pricing.launch.b2': 'Підключення домену + запуск',
    'pricing.launch.b3': 'Форма заявок і сповіщення',
    'pricing.launch.cta': 'Запустити за €299',
    // How It Works
    'how.title': 'Як це',
    'how.titleHighlight': 'працює',
    'how.step1.title': 'Розкажіть про ваш проєкт',
    'how.step1.note': 'Займе 2 хвилини',
    'how.step2.title': 'Ми створюємо демо',
    'how.step2.note': 'Оплата не потрібна',
    'how.step3.title': 'Ви надсилаєте правки',
    'how.step3.note': 'Доопрацьовуємо, доки вам не сподобається',
    'how.step4.title': 'Ви затверджуєте та оплачуєте €299',
    'how.step4.note': 'Лише коли ви повністю задоволені',
    'how.step5.title': 'Підключаємо домен і запускаємо сайт',
    'how.step5.note': 'Сайт публікується та починає працювати',
    'how.note': 'Якщо демо не сподобається — ви не платите.',
    // FAQ
    'faq.title': 'Часті',
    'faq.titleHighlight': 'питання',
    'faq.subtitle': 'Все, що потрібно знати про роботу з нами',
    'faq.q1': 'Що таке безкоштовне демо?',
    'faq.a1': 'Це попередня версія вашого сайту, заснована на вашому бізнесі та цілях. Ви дивитесь її до оплати.',
    'faq.q2': 'Як швидко я отримаю демо?',
    'faq.a2': 'Ви отримаєте демо-версію через 1–3 дні.',
    'faq.q3': 'Чи можна просити правки в демо?',
    'faq.a3': 'Так. Надсилайте фідбек — ми оновимо демо для вас.',
    'faq.q4': 'Що відбувається після оплати €299?',
    'faq.a4': 'Ми фіналізуємо сайт, публікуємо його, підключаємо ваш домен і вмикаємо форму заявок.',
    'faq.q5': 'Ви працюєте по всьому світу?',
    'faq.a5': 'Так. Ми працюємо з клієнтами з будь-якої країни та будь-якої сфери.',
    // Testimonials
    'testimonials.title': 'Що кажуть',
    'testimonials.titleHighlight': 'клієнти',
    'testimonials.subtitle': 'Реальні відгуки від бізнесів, яким ми допомогли вийти онлайн',
    'testimonials.r1.name': 'Михайло',
    'testimonials.r1.text': 'Неймовірна цінність за €299! Отримав приголомшливий сайт усього за 2 дні. Якість перевершила всі мої очікування.',
    'testimonials.r1.business': 'Власник фітнес-студії',
    'testimonials.r2.name': 'Анна',
    'testimonials.r2.text': 'Спочатку сумнівалася, але безкоштовне демо переконало мене. Професійний дизайн, швидкі терміни, чудова ціна!',
    'testimonials.r2.business': 'Власниця пекарні',
    'testimonials.r3.name': 'Дмитро',
    'testimonials.r3.text': 'Найкраще рішення для мого бізнесу. €299 за таку якість — це неймовірно. Вже отримую нових клієнтів із сайту!',
    'testimonials.r3.business': 'Фотостудія',
    // Consultation
    'consultation.title': 'Отримайте ваше',
    'consultation.titleHighlight': 'безкоштовне демо',
    'consultation.subtitle': 'Скажіть, що вам потрібно — ми надішлемо посилання на демо.',
    'consultation.benefit1': 'Безкоштовний перегляд',
    'consultation.benefit2': 'Швидка доставка',
    'consultation.benefit3': "Без зобов'язань",
    'consultation.name': "Ім'я",
    'consultation.namePlaceholder': "Ваше ім'я",
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Опис проєкту',
    'consultation.descPlaceholder': 'Розкажіть про ваш бізнес і який сайт вам потрібен...',
    'consultation.cta': 'Отримати безкоштовне демо',
    'consultation.note': "Без зобов'язань. Якщо демо не сподобається — ви не платите.",
    'consultation.sending': 'Відправка...',
    'consultation.or': 'або напишіть нам напряму',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Послуги',
    'footer.company': 'Компанія',
    'footer.legal': 'Юридичне',
    'footer.contact': 'Контакти',
    'footer.rights': 'Всі права захищені.',
    'footer.aboutLink': 'Про IT Monsters',
    // Stats
    'stats.websites': 'Сайтів запущено',
    'stats.clients': 'Задоволених клієнтів',
    'stats.years': 'Років досвіду',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вплив',
    'stats.subtitle': 'Результати для бізнесу по всьому світу'
  },
  pl: {
    // Navigation
    'nav.getStarted': 'Otrzymaj darmowe demo',
    'nav.home': 'Strona główna',
    'nav.services': 'Cennik',
    'nav.referral': 'Program poleceń',
    'nav.privacy': 'Polityka prywatności',
    'nav.terms': 'Warunki użytkowania',
    // Cookie Banner
    'cookie.title': 'Używamy plików cookie',
    'cookie.description': 'Używamy plików cookie, aby poprawić Twoje doświadczenia na naszej stronie, analizować ruch i personalizować treści.',
    'cookie.accept': 'Zaakceptuj wszystkie',
    'cookie.decline': 'Odrzuć',
    'cookie.learnMore': 'Dowiedz się więcej',
    // Hero
    'hero.badge': 'Profesjonalna agencja internetowa',
    'hero.title': 'Nowoczesne strony',
    'hero.titleHighlight': 'za €299',
    'hero.subtitle': 'Najpierw otrzymaj darmowe demo. Płacisz tylko, jeśli chcesz uruchomić stronę.',
    'hero.bullet1': 'Darmowe demo przed płatnością',
    'hero.bullet2': 'Jednorazowa opłata: €299',
    'hero.bullet3': 'Pracujemy na całym świecie',
    'hero.cta': 'Otrzymaj darmowe demo',
    'hero.referral': 'Program poleceń',
    // Pricing
    'pricing.title': 'Jak działają',
    'pricing.titleHighlight': 'ceny',
    'pricing.subtitle': 'Najpierw darmowe demo. Płacisz tylko, jeśli chcesz uruchomić stronę.',
    'pricing.demo.title': 'Darmowe demo',
    'pricing.demo.label': 'DARMOWE',
    'pricing.demo.desc': 'Zobacz swoją stronę przed płatnością.',
    'pricing.demo.b1': 'Indywidualne demo pod Twój projekt',
    'pricing.demo.b2': 'Bez zobowiązań',
    'pricing.demo.b3': 'Jeśli demo Ci się nie spodoba — nie płacisz',
    'pricing.demo.cta': 'Otrzymaj darmowe demo',
    'pricing.launch.title': 'Uruchomienie strony',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(jednorazowo)',
    'pricing.launch.desc': 'Opublikujemy stronę i pomożemy zacząć zbierać zapytania.',
    'pricing.launch.b1': 'Nowoczesny design (desktop + mobile)',
    'pricing.launch.b2': 'Podłączenie domeny i publikacja',
    'pricing.launch.b3': 'Formularz kontaktowy + powiadomienia',
    'pricing.launch.cta': 'Uruchom stronę za €299',
    // How It Works
    'how.title': 'Jak to',
    'how.titleHighlight': 'działa',
    'how.step1.title': 'Opowiedz nam o swoim projekcie',
    'how.step1.note': 'Zajmie ok. 2 min',
    'how.step2.title': 'Przygotowujemy Twoje demo',
    'how.step2.note': 'Bez opłat',
    'how.step3.title': 'Prosisz o poprawki',
    'how.step3.note': 'Wprowadzamy poprawki, aż będziesz zadowolony',
    'how.step4.title': 'Akceptujesz i płacisz €299',
    'how.step4.note': 'Tylko gdy jesteś w 100% zadowolony',
    'how.step5.title': 'Podłączamy domenę i publikujemy stronę',
    'how.step5.note': 'Twoja strona jest online',
    'how.note': 'Jeśli demo Ci się nie spodoba — nie płacisz.',
    // FAQ
    'faq.title': 'Często zadawane',
    'faq.titleHighlight': 'pytania',
    'faq.subtitle': 'Wszystko, co musisz wiedzieć o pracy z nami',
    'faq.q1': 'Czym jest darmowe demo?',
    'faq.a1': 'To podgląd Twojej strony przygotowany na podstawie krótkiego opisu. Otrzymujesz link i oceniasz projekt bez płatności.',
    'faq.q2': 'Jak szybko otrzymam demo?',
    'faq.a2': 'Zwykle w 1–3 dni robocze. Wyślemy link, gdy tylko demo będzie gotowe.',
    'faq.q3': 'Czy mogę poprosić o zmiany w wersji demo?',
    'faq.a3': 'Tak — wprowadzamy poprawki, aż projekt będzie Ci odpowiadał. Dopiero potem decydujesz, czy uruchamiamy stronę.',
    'faq.q4': 'Co się dzieje po opłaceniu €299?',
    'faq.a4': 'Publikujemy stronę, podłączamy domenę i uruchamiamy formularz kontaktowy z powiadomieniami. Strona jest gotowa do pozyskiwania klientów.',
    'faq.q5': 'Czy pracujecie na całym świecie?',
    'faq.a5': 'Tak — działamy zdalnie i realizujemy projekty dla firm z różnych krajów.',
    // Testimonials
    'testimonials.title': 'Co mówią',
    'testimonials.titleHighlight': 'klienci',
    'testimonials.subtitle': 'Prawdziwe opinie od firm, którym pomogliśmy wejść online',
    'testimonials.r1.name': 'Michał',
    'testimonials.r1.text': 'Niesamowita jakość w tej cenie! Otrzymałem świetną stronę w zaledwie 2 dni. Jakość przekroczyła moje oczekiwania.',
    'testimonials.r1.business': 'Właściciel studia fitness',
    'testimonials.r2.name': 'Kasia',
    'testimonials.r2.text': 'Na początku byłam sceptyczna, ale darmowe demo mnie przekonało. Profesjonalny design, szybka dostawa, świetna cena!',
    'testimonials.r2.business': 'Właścicielka piekarni',
    'testimonials.r3.name': 'Tomek',
    'testimonials.r3.text': 'Najlepsza decyzja dla mojego biznesu. €299 za taką jakość to niewiarygodne. Już dostaję nowych klientów ze strony!',
    'testimonials.r3.business': 'Studio fotograficzne',
    // Consultation
    'consultation.title': 'Otrzymaj',
    'consultation.titleHighlight': 'darmowe demo',
    'consultation.subtitle': 'Napisz, czego potrzebujesz — wyślemy Ci link do wersji demo.',
    'consultation.benefit1': 'Darmowy podgląd',
    'consultation.benefit2': 'Szybka dostawa',
    'consultation.benefit3': 'Bez zobowiązań',
    'consultation.name': 'Imię',
    'consultation.namePlaceholder': 'Twoje imię',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'twoj@email.com',
    'consultation.desc': 'Opis projektu',
    'consultation.descPlaceholder': 'Opowiedz nam o swoim biznesie i o tym, jakiej strony potrzebujesz…',
    'consultation.cta': 'Otrzymaj darmowe demo',
    'consultation.note': 'Bez zobowiązań. Jeśli demo Ci się nie spodoba — nie płacisz.',
    'consultation.sending': 'Wysyłanie...',
    'consultation.or': 'lub napisz do nas bezpośrednio',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Usługi',
    'footer.company': 'Firma',
    'footer.legal': 'Prawne',
    'footer.contact': 'Kontakt',
    'footer.rights': 'Wszelkie prawa zastrzeżone.',
    'footer.aboutLink': 'O IT Monsters',
    // Stats
    'stats.websites': 'Uruchomionych stron',
    'stats.clients': 'Zadowolonych klientów',
    'stats.years': 'Lat doświadczenia',
    'stats.title': 'Nasz',
    'stats.titleHighlight': 'Wpływ',
    'stats.subtitle': 'Wyniki dla firm na całym świecie'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/ru')) {
      setLanguage('ru');
    } else if (path.startsWith('/ua')) {
      setLanguage('ua');
    } else if (path.startsWith('/uk')) {
      // Handle legacy 'uk' prefix as 'ua'
      setLanguage('ua');
    } else if (path.startsWith('/pl')) {
      setLanguage('pl');
    } else {
      setLanguage('en');
    }
  }, []);
  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)['en']] || key;
  };
  const getPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    if (language === 'ru') {
      return `/ru/${cleanPath}`;
    } else if (language === 'ua') {
      return `/ua/${cleanPath}`;
    } else if (language === 'pl') {
      return `/pl/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    getPath
  }} data-id="element-289">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}